import { useState } from 'react'

import { api, getToken } from '../api/client'
import type { ReportInfo, ReportResult } from '../api/types'
import { Card, EmptyState, ErrorBanner, formatDateTime, Spinner } from '../components/common'
import { DataTable, type Column } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Report standard del tenant.
 *
 * Il catalogo mostra la **domanda** e non solo il titolo, perché un titolo da solo
 * non basta: «Beni senza contratto» non dice se comprende quelli dismessi, e chi lo
 * legge senza saperlo prende decisioni su un numero che ha capito male. Le
 * esclusioni sono parte della risposta.
 */
type RigaReport = ReportResult['rows'][number]

/**
 * The columns of a report result, as the server declares them. A column whose
 * cells are all numbers sorts and aligns as numbers; the others stay monospace.
 */
function colonneRisultato(dati: ReportResult): Column<RigaReport>[] {
  return dati.columns.map((nome, j) => {
    const numerica = dati.rows.every((row) => typeof row[j] === 'number' || row[j] === '' || row[j] == null)
      && dati.rows.some((row) => typeof row[j] === 'number')
    return {
      // The name keeps the column choices of one report apart from another's.
      key: `${j}:${nome}`,
      header: nome.replace(/_/g, ' '),
      type: numerica ? 'number' : undefined,
      mono: !numerica,
      value: (row) => row[j],
      render: (row) => {
        const cell = row[j]
        return typeof cell === 'number' ? cell.toLocaleString(localeCorrente()) : cell === '' ? '—' : cell
      },
    }
  })
}

export default function Reports() {
  const catalog = useAsync(() => api.reports(), [])
  const [active, setActive] = useState<string | null>(null)
  const [days, setDays] = useState(90)
  const [error, setError] = useState<unknown>(null)
  const [exporting, setExporting] = useState<string | null>(null)

  const report = useAsync(
    () => (active ? api.report(active, days) : Promise.resolve(null)),
    [active, days],
  )

  async function download(key: string) {
    setExporting(key)
    setError(null)
    try {
      const resp = await fetch(`/api/v1${api.reportCsvPath(key, days)}`, {
        headers: { Authorization: `Bearer ${getToken() ?? ''}` },
      })
      if (!resp.ok) throw new Error(t('Esportazione non riuscita (HTTP {status})', { status: resp.status }))
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `censo-${key}.csv`
      a.click()
      // Senza revoca il blob resta in memoria per tutta la vita della pagina.
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(err)
    } finally {
      setExporting(null)
    }
  }

  const info = catalog.data?.find((r) => r.key === active)

  const colonneCatalogo: Column<ReportInfo>[] = [
    {
      key: 'title',
      header: t('Report'),
      value: (r) => t(r.title),
      render: (r) => (
        <>
          <strong>{t(r.title)}</strong>
          {r.windowed && (
            <div className="text-muted" style={{ fontSize: 11 }}>
              {t('su un periodo')}
            </div>
          )}
        </>
      ),
    },
    {
      key: 'question',
      header: t('A cosa risponde'),
      value: (r) => t(r.question),
      render: (r) => <span className="text-muted">{t(r.question)}</span>,
    },
    {
      key: 'azioni',
      header: t('Azioni'),
      sort: false,
      searchable: false,
      render: (r) => (
        <div className="rowactions">
          <button className="secondary" onClick={() => setActive(r.key)}>
            {t('Apri')}
          </button>
          <button className="secondary" disabled={exporting === r.key} onClick={() => download(r.key)}>
            {exporting === r.key ? '…' : 'CSV'}
          </button>
        </div>
      ),
    },
  ]

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Report')}</h1>
          <p className="page__subtitle">
            {t('Report predefiniti su inventario, asset, contratti e discovery, con ambito ed esclusioni espliciti per evitare risultati fuorvianti.')}
          </p>
        </div>
      </header>

      <ErrorBanner error={error ?? catalog.error ?? report.error} />

      <Card title={t('Catalogo')}>
        {catalog.loading && !catalog.data ? (
          <Spinner />
        ) : (
          <DataTable
            rows={catalog.data ?? []}
            columns={colonneCatalogo}
            rowKey={(r) => r.key}
            conteggio={{ nome: t('report') }}
          />
        )}
      </Card>

      {active && (
        <Card title={info?.title ? t(info.title) : active}>
          <p className="field__hint" style={{ marginTop: 0 }}>
            {info?.question ? t(info.question) : null}
          </p>

          {/* La finestra si mostra solo dove cambia qualcosa: offrirla su un
              report di consistenza suggerirebbe che il risultato dipenda dal
              periodo, e chi lo cambiasse senza effetto sospetterebbe un guasto. */}
          {info?.windowed && (
            <div className="field" style={{ maxWidth: 220 }}>
              <label htmlFor="r-days">{t('Finestra')}</label>
              <select id="r-days" value={days} onChange={(e) => setDays(Number(e.target.value))}>
                <option value={30}>{t('30 giorni')}</option>
                <option value={90}>{t('90 giorni')}</option>
                <option value={180}>{t('180 giorni')}</option>
                <option value={365}>{t('1 anno')}</option>
              </select>
            </div>
          )}

          {report.loading && !report.data ? (
            <Spinner />
          ) : !report.data?.rows.length ? (
            <EmptyState
              title={t('Nessuna riga')}
              hint={t("Non è un errore: con questi criteri non c'è nulla da riportare. È spesso la risposta che si spera.")}
            />
          ) : (
            <>
              <DataTable
                rows={report.data.rows}
                columns={colonneRisultato(report.data)}
                rowKey={(_row, i) => String(i)}
                nomeEsportazione={report.data.key}
              />
              <p className="field__hint">
                {/* Singolare e plurale: «1 righe» è il genere di dettaglio che fa
                    dubitare del resto dei numeri sulla pagina. */}
                {report.data.rows.length === 1
                  ? t('{n} riga · generato il {quando}', { n: 1, quando: formatDateTime(report.data.generated_at) })
                  : t('{n} righe · generato il {quando}', { n: report.data.rows.length, quando: formatDateTime(report.data.generated_at) })}
              </p>
            </>
          )}
        </Card>
      )}
    </>
  )
}
